/**
 * cro-home.js — CRO home pages (index, index.v2, index.v3): hero slideshow (story bars / peek
 * cards, autoplay, deferred banners), product tabs + quick add, and the editorial cover's search
 * and cart icons. Sheets, cart, copy-code and wishlist come from cro-core.
 * Everything works without this file: banners swipe natively (scroll-snap) and link, and the
 * first product tab is shown.
 */
import { $, $$, initQuickAdd, openCart, openSearch, refreshWishlist, track } from '@theme/cro-core';

$$('[data-cro-hero]').forEach(initHero);
$$('[data-cro-hp]').forEach(initTabs);
$$('.cro-cover [data-cro-search]').forEach((b) => b.addEventListener('click', openSearch));
$$('.cro-cover [data-cro-cart]').forEach((b) => b.addEventListener('click', openCart));
refreshWishlist();

/* ------------------------------------------------------------------ hero */
function initHero(root) {
  const strip = $('[data-cro-hero-track]', root);
  const slides = strip ? $$('.cro-hero__slide', strip) : [];
  const bars = $$('.cro-hero__bars i', root);
  if (slides.length < 2) return;
  let current = 0;

  // Banners 2+ carry their URLs in data-*: load one ahead of the banner on screen.
  const hydrate = (k) => {
    const img = slides[k] && slides[k].querySelector('img[data-src]');
    if (!img) return null;
    img.srcset = img.dataset.srcset || '';
    img.src = img.dataset.src;
    img.removeAttribute('data-src');
    img.removeAttribute('data-srcset');
    return img;
  };
  const hydrateAll = () => slides.forEach((_, k) => hydrate(k));

  const setActive = (k) => {
    current = k;
    bars.forEach((b, j) => {
      b.classList.toggle('is-done', j < k);
      b.classList.toggle('is-on', j === k);
    });
    hydrate(k + 1);
  };

  // Whatever moved the strip (a swipe or autoplay), the banner mostly in view is the current one.
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting && en.intersectionRatio > 0.6) {
            const k = slides.indexOf(en.target);
            if (k !== current) setActive(k);
          }
        });
      },
      { root: strip, threshold: [0.6] }
    );
    slides.forEach((s) => io.observe(s));
  }

  // Scroll to a banner's own position: full-width banners and 86% "peek" cards alike.
  const go = (k) => {
    hydrate(k);
    setActive(k);
    strip.scrollTo({ left: slides[k].offsetLeft - slides[0].offsetLeft, behavior: 'smooth' });
  };

  /* Autoplay: the active bar fills over --cro-hero-t (CSS); when it's full, move on. It starts
     once banner 1 has loaded and banner 2 is ready, pauses off screen / in a background tab and
     stops for good at the shopper's first touch. */
  let auto = root.hasAttribute('data-autoplay') && !matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (!auto) root.classList.add('is-manual');
  const stop = () => {
    if (!auto) return;
    auto = false;
    root.classList.remove('is-auto');
    root.classList.add('is-manual');
  };
  root.addEventListener('animationend', (e) => {
    const bar = e.target.parentElement;
    if (!auto || e.animationName !== 'cro-hero-bar' || !bar || !bar.classList.contains('is-on')) return;
    go((current + 1) % slides.length);
  });
  const inView = { screen: true, tab: !document.hidden };
  const pause = () => root.classList.toggle('is-paused', !(inView.screen && inView.tab));
  if ('IntersectionObserver' in window) {
    new IntersectionObserver((en) => {
      inView.screen = en[0].isIntersecting;
      pause();
    }, { threshold: 0.35 }).observe(root);
  }
  document.addEventListener('visibilitychange', () => {
    inView.tab = !document.hidden;
    pause();
  });

  let touched = false;
  const onTouch = () => {
    hydrateAll();
    stop();
    if (!touched) {
      touched = true;
      track('cro_home_hero_touch', { banner: current + 1 });
    }
  };
  strip.addEventListener('pointerdown', onTouch, { passive: true });
  strip.addEventListener('wheel', onTouch, { passive: true });
  strip.addEventListener('click', (e) => {
    if (e.target.closest('[data-cro-hero-link]')) track('cro_home_banner_click', { banner: current + 1 });
  });

  const first = slides[0].querySelector('.cro-hero__img');
  const start = () => {
    if (!auto) return;
    const second = hydrate(1) || slides[1].querySelector('img');
    const run = () => auto && root.classList.add('is-auto');
    if (second && !second.complete) {
      second.addEventListener('load', run, { once: true });
      second.addEventListener('error', run, { once: true });
    } else run();
  };
  // A second after banner 1 has painted, so banner 2 never competes with it.
  const afterFirst = () => setTimeout(start, 1000);
  if (!first || first.complete) afterFirst();
  else {
    first.addEventListener('load', afterFirst, { once: true });
    first.addEventListener('error', afterFirst, { once: true });
  }
}

/* ------------------------------------------------------------------ product tabs */
function initTabs(root) {
  const tabs = $$('[data-cro-tab]', root);
  const select = (t) => {
    tabs.forEach((x) => {
      const on = x === t;
      x.setAttribute('aria-selected', on ? 'true' : 'false');
      x.tabIndex = on ? 0 : -1;
      const panel = document.getElementById(x.getAttribute('aria-controls'));
      if (panel) panel.hidden = !on;
    });
  };
  tabs.forEach((t, i) => {
    t.tabIndex = i === 0 ? 0 : -1;
    t.addEventListener('click', () => {
      select(t);
      track('cro_home_tab', { tab: t.textContent.trim() });
    });
    t.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
      const n = tabs[(i + (e.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
      select(n);
      n.focus();
    });
  });
  initQuickAdd(root, $('[data-cro-qa-sheet]', root));
}
